/* Handing a link OUT to another app.
 *
 * The opposite direction from `share.ts`, which lands shares coming *in* from
 * Android's share sheet. Separate files because they have nothing in common but
 * the word: that one is a module-evaluation side effect that runs once per load,
 * this one is a control's click handler.
 *
 * `navigator.share` is the good path and the only one that reaches a phone's
 * share sheet, but it is absent on desktop Firefox and unavailable outside a
 * secure context — and the control that calls this renders at both breakpoints,
 * so the clipboard fallback is a routine path rather than an edge case. The
 * clipboard shape is `settings-webhook`'s, which had the same problem first:
 * write, then flag the success for long enough to be read.
 */

export type LinkShareOutcome = 'shared' | 'copied' | 'failed';

/**
 * Share `url`, else copy it. Must be called from a user gesture — both APIs
 * require one.
 *
 * Opening the share sheet and dismissing it rejects with `AbortError`, which is
 * reported as success and deliberately does NOT fall through to the clipboard:
 * the user said no, and silently copying instead answers a question they
 * declined to answer.
 */
export async function shareLink(data: {
  title?: string;
  url: string;
}): Promise<LinkShareOutcome> {
  if (navigator.share) {
    try {
      await navigator.share(data);
      return 'shared';
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return 'shared';
      // Anything else — no handler registered, a policy refusal — is worth
      // falling through for, because the clipboard still gets the job done.
    }
  }
  try {
    await navigator.clipboard.writeText(data.url);
    return 'copied';
  } catch {
    return 'failed';
  }
}
